import { useState, useEffect, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Clock, CheckCircle, XCircle, Loader2, Activity } from 'lucide-react';
import { serverApi } from '../services/api';
import type { OwnerActionLogSummary } from '../types/server';

const typeColors: Record<string, string> = {
  create: 'text-green-400',
  start: 'text-blue-400',
  stop: 'text-yellow-400',
  restart: 'text-purple-400',
  delete: 'text-red-400',
  provision: 'text-cyan-400',
};

const statusIcons: Record<string, ReactNode> = {
  pending: <Clock className="w-4 h-4 text-gray-400" />,
  'in-progress': <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />,
  completed: <CheckCircle className="w-4 h-4 text-green-400" />,
  failed: <XCircle className="w-4 h-4 text-red-400" />,
};

export function ActivityLog({ serverId }: { serverId: string }) {
  const [logs, setLogs] = useState<OwnerActionLogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchLogs = async () => {
      try {
        const res = await serverApi.getActionLogs(serverId, 20);
        if (!cancelled && res.success) {
          setLogs(res.logs);
        }
      } catch {
        // silently ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLogs();
    const interval = setInterval(fetchLogs, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [serverId, isOpen]);

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDuration = (start: string, end?: string) => {
    const ms = (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime();
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  return (
    <div className="card">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-dark-600/50 transition-colors"
      >
        <h3 className="section-title flex items-center gap-2 mb-0">
          <Activity className="w-5 h-5 text-gray-400" />
          Activity Log
        </h3>
        {isOpen ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>

      {isOpen && (
        <div className="px-4 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No activity yet</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log, index) => (
                <div key={`${log.type}:${log.startedAt}:${index}`} className="border border-dark-500 rounded-lg overflow-hidden">
                  <div className="w-full px-3 py-2 flex items-center gap-3 text-left">
                    {statusIcons[log.status]}
                    <span className={`font-medium text-sm uppercase ${typeColors[log.type] || 'text-gray-300'}`}>
                      {log.type}
                    </span>
                    <span className="text-xs text-gray-500 ml-auto">
                      {formatTime(log.startedAt)}
                      {' · '}
                      {formatDuration(log.startedAt, log.completedAt)}
                    </span>
                  </div>
                  {log.error && (
                    <div className="border-t border-dark-500 bg-red-900/20 px-3 py-2 text-xs font-medium text-red-400">
                      {log.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
